import React, { useContext, useRef, useState } from "react";
import axios from "axios";
import { CanvasContext, ICanvasComponent } from "../canvasContext.tsx";
import { GetMyUploadsCall } from "../../redux/api/uploads.api";
import { basicURL } from "../../apiManager/APIConfig";
import { store } from "../../redux/store";

const VideoElement = (props: ICanvasComponent) => {
  const { content, id, isPlayMode } = props;
  const { actions, uploadEntity, uploadsOnlyFromLibrary } = useContext(CanvasContext);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerItems, setPickerItems] = useState<any[]>([]);

  const uploadToBackend = async (file: File): Promise<string | null> => {
    if (!uploadEntity) return null;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("entity_type", uploadEntity.entity_type);
    formData.append("entity_id", uploadEntity.entity_id);
    formData.append("destination", "presentation");
    formData.append("folder_name", "presentation");
    const token = store.getState().auth?.access_token;
    const res = await axios.post(`${basicURL}file/upload`, formData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res?.data?.download_url || null;
  };

  const handleUrlBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const url = e.target?.value?.trim();
    if (url) {
      actions?.updateCanvasData({ id, content: url });
      actions?.saveNow?.();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target?.files?.[0];
    if (!file || !uploadEntity) return;
    setUploading(true);
    try {
      const url = await uploadToBackend(file);
      if (url) {
        actions?.updateCanvasData({ id, content: url });
        actions?.saveNow?.();
      }
    } catch (err) {
      console.error("Video upload error:", err);
      const message =
        (err as any)?.response?.data?.message ||
        "Upload failed. Please try again.";
      if (typeof window !== "undefined") {
        window.alert(message);
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (!content) {
    if (isPlayMode) {
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            background: "#0f172a",
            borderRadius: 4,
          }}
        />
      );
    }
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "#f0f0f0",
          borderRadius: 4,
          padding: 8,
        }}
      >
        {!uploadsOnlyFromLibrary && (
          <>
            <span style={{ fontSize: 12, marginBottom: 8 }}>Video URL or upload</span>
            <input
              ref={inputRef}
              type="url"
              placeholder="Paste video URL..."
              style={{ width: "100%", padding: 4, fontSize: 12, marginBottom: 8 }}
              onBlur={handleUrlBlur}
            />
            {uploadEntity && (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  accept="video/*"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
                <button
                  type="button"
                  style={{ fontSize: 12, padding: "4px 8px" }}
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? "Uploading…" : "Upload video"}
                </button>
              </>
            )}
          </>
        )}
        {uploadsOnlyFromLibrary && (
          <span style={{ fontSize: 12, marginBottom: 8, color: "#64748b" }}>
            Use the Uploads panel to add videos
          </span>
        )}
        <button
          type="button"
          style={{
            marginTop: 8,
            fontSize: 12,
            padding: "4px 8px",
            borderRadius: 999,
            border: "1px solid #cbd5e1",
            background: "#fff",
            cursor: "pointer",
          }}
          onClick={async () => {
            try {
              setShowPicker(true);
              setPickerLoading(true);
              const items = await GetMyUploadsCall("user_upload");
              setPickerItems(items.filter((f: any) => f.type?.startsWith("video/")));
            } catch (e) {
              console.error("Failed to load video uploads for picker", e);
            } finally {
              setPickerLoading(false);
            }
          }}
        >
          Pick from uploads
        </button>
      </div>
    );
  }

  return (
    <>
      <div style={{ width: "100%", height: "100%", background: "#000" }}>
        <video
          src={content}
          controls
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
          muted
          loop
          playsInline
        />
      </div>
      {showPicker && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1300,
          }}
          onClick={() => setShowPicker(false)}
        >
          <div
            style={{
              width: 640,
              maxWidth: "90vw",
              maxHeight: "80vh",
              background: "#fff",
              borderRadius: 16,
              padding: 16,
              boxShadow: "0 24px 60px rgba(15,23,42,0.38)",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 4,
              }}
            >
              <span style={{ fontWeight: 600, fontSize: 14 }}>My uploads</span>
              <button
                type="button"
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: 18,
                }}
                onClick={() => setShowPicker(false)}
              >
                ×
              </button>
            </div>
            {pickerLoading ? (
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                }}
              >
                Loading…
              </div>
            ) : pickerItems.length === 0 ? (
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                }}
              >
                No uploads yet. Use the Uploads page to add private videos.
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                  gap: 10,
                  overflowY: "auto",
                }}
              >
                {pickerItems.map((file: any) => (
                  <button
                    key={file.id || file.url}
                    type="button"
                    onClick={() => {
                      actions?.updateCanvasData({
                        id,
                        content: file.url,
                      });
                      actions?.saveNow?.();
                      setShowPicker(false);
                    }}
                    style={{
                      border: "none",
                      padding: 0,
                      borderRadius: 12,
                      overflow: "hidden",
                      cursor: "pointer",
                      background: "#020617",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        height: 90,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#e5e7eb",
                        fontSize: 12,
                      }}
                    >
                      {file.alias || file.name || "Video"}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default VideoElement;
